import React, { useEffect } from "react";
import { getActualCardWarnings } from "./actualComplianceWarnings";
import ShiftInputsModal from "./ShiftInputsModal";
import WarningIcon from "@mui/icons-material/Warning";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import Tooltip from "@mui/material/Tooltip";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import LoadSpinner from "../../../../utilities/LoadSpinner";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CloseIcon from "@mui/icons-material/Close";
import {
  getCardColor,
  getCardColor2,
  getRoleType,
  get_t_str,
  get_t_str_shortened,
  get_t_str_shortened_minute_precision,
  business_decline_suffix,
} from "../../../../helpers/helpers";
import Filter2Icon from "@mui/icons-material/Filter2";
import Filter3Icon from "@mui/icons-material/Filter3";
import useLongPress from "../../../../helpers/useLongPress";
import useAutoScroll from "../../../../helpers/useAutoScroll";
import {
  getLaborLawViolationLabel,
  getShiftLaborLawViolations,
} from "./shiftLaborLawWarnings";
import {
  getMissedShiftReasonLabel,
  getMissedShiftReason,
  MISSED_SHIFT_REASON_LABELS,
} from "./attendanceCoverage";

// A no-break space before the dash and an ordinary one after: the label can
// still wrap when a card is narrow, but only in one place, so a time range
// never splits into three lines.
const NON_BREAKING_DASH = "\u00a0- ";

function ShiftCard(props) {
  const windowWidth = props.windowWidth ?? window.innerWidth;
  // Auto-scroll hook
  const { startDragging, stopDragging } = useAutoScroll();

  // Opens the modal when true
  const [open, setOpen] = React.useState(false);

  // Shows the copy button for copy and paste when true
  const [showActionButtons, setShowActionButtons] = React.useState(false);
  // Copying a shift and then switching to actuals would otherwise leave the
  // source card's menu open over times it has no business editing.
  const actionButtonsOpen = showActionButtons && !props.showActualTimes;

  React.useEffect(() => {
    setShowActionButtons(props.shift_info?.id === props.copiedShift?.id);
  }, [props.shift_info]);

  React.useEffect(() => {
    if (props.setShowBreak && !props.shift_info?.is_copilot_suggestion) {
      props.setShowBreak(!showActionButtons);
    }
  }, [showActionButtons]);

  // Shows the check when they have successfully copied a shift when true
  const [showCopiedCheck, setShowCopyCheck] = React.useState(false);

  // Shows the check when they have successfully cut a shift when true
  const [showCutCheck, setShowCutCheck] = React.useState(false);

  // When dragging over with copied shift
  const [isDraggedOver, setIsDraggedOver] = React.useState(false);

  // Loading state for transferring shifts
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    if (props.saveShiftFinished) {
      setIsLoading(false);
    }
  }, [props.saveShiftFinished]);

  // Function that handles the opening of the modal
  const handleOpen = () => {
    setOpen(!props.locked_mode);
  };

  // Function that handles the closing of the modal
  const handleClose = () => {
    setOpen(false);
    props.setSaveShiftFinished(false);
    setShowActionButtons(false);
  };

  // Function that handles the context menu
  const handleContextMenu = (event) => {
    event.preventDefault();
    // Copy and cut rewrite the schedule, and actual mode is a read of what
    // happened, so the card's action menu does not open there.
    if (props.showActualTimes) return;
    if (props.shifts_info?.length > 1 && props.weekly_or_daily === "weekly") return;
    if (showActionButtons) {
      props.setCopiedShift(null);
      setShowActionButtons(false);
    } else {
      setShowActionButtons((prevState) => !prevState);
    }
  };

  // Function that handles the copying of a shift
  const handleCopy = (shift) => {
    if (!showCopiedCheck) {
      // Create a copy of the shift object and set the copy_type to "copy"
      const updatedShift = { ...shift, copy_type: "copy" };
      props.setCopiedShift(updatedShift); // Set the copied shift state
      setShowCopyCheck(true);
      setShowCutCheck(false);
    } else {
      props.setCopiedShift(null); // Reset the copied shift state
      setShowCopyCheck(false);
      setShowCutCheck(false);
    }
  };

  // Function that handles the cutting of a shift
  const handleCut = (shift) => {
    if (!showCutCheck) {
      // Create a copy of the shift object and set the copy_type to "cut"
      const updatedShift = { ...shift, copy_type: "cut" };
      props.setCopiedShift(updatedShift); // Set the copied shift state
      setShowCutCheck(true);
      setShowCopyCheck(false);
    } else {
      props.setCopiedShift(null); // Reset the copied shift state
      setShowCutCheck(false);
      setShowCutCheck(false);
    }
  };

  // Hides the copy button and copy check when props.copiedShift changes
  useEffect(() => {
    if (props.copiedShift && props.copiedShift.id !== props.shift_info.id) {
      // Compare the shift IDs instead of the entire object
      setShowActionButtons(false);
      setShowCopyCheck(false);
      setShowCutCheck(false);
    }
  }, [props.copiedShift, props.shift_info]);

  // Function that gets the role name
  const getRoleName = (role_id, role_lookups) => {
    const role_info = role_lookups.filter((r) => r.id === role_id);
    if (role_info.length > 0) {
      return role_info[0]["role_name"];
    } else {
      return null;
    }
  };

  // In actual mode the shade says whether the shift is over: one still running
  // keeps the lighter base colour, a finished one takes the darker. Outside
  // actual mode the darker shade still means "not editable", as before.
  const cardColorFor = (shift) => {
    // The card carries its own state. Looking it up by published shift left an
    // unscheduled punch, which has none, shaded as though it had finished while
    // its label read "now".
    const actual = props.showActualTimes ? shift : null;
    const useDarkerShade = props.showActualTimes
      ? !actual?.in_progress
      : props.locked_mode;
    // The last argument adds cursor-pointer. Only the darker shade skips it, so
    // in actual mode a shift still running was the one card advertising a click
    // that does nothing. Nothing here is clickable.
    return getCardColor2(
      shift?.role_id,
      props.role_names,
      useDarkerShade,
      !props.showActualTimes
    );
  };

  const shiftCardColor = cardColorFor(props.shift_info);

  // Simplified drag start function
  const handleDragStart = (e, shift) => {
    console.log('handleDragStart called');
    if (shift?.is_copilot_suggestion) {
      e.preventDefault();
      return;
    }

    props.setCopiedShift(shift);
    startDragging(); // Mark that we're starting to drag and add mouse listeners
  };

  const handleDragOver = (e) => {
    e.preventDefault(); // This is necessary to allow dropping
    setIsDraggedOver(true);
  };

  const handleDragLeave = (e) => {
    // Prevent the event from being handled by parent elements
    e.preventDefault();
    e.stopPropagation();

    // Only reset isDraggedOver if we're not entering a child element
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDraggedOver(false);
    }
  };

  const handleDragEnd = (e) => {
    // Always stop dragging and auto-scrolling when drag ends
    stopDragging();
    props.setCopiedShift(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    stopDragging(); // Stop dragging state and auto-scrolling

    const draggedShift = props.copiedShift;
    const targetShift = props.shift_info;

    if (draggedShift.id === targetShift.id) {
      setIsDraggedOver(false);
      return; // Do nothing if dropping on the same shift
    }

    // Swap the relevant information
    const newDraggedShift = { ...draggedShift };
    const newTargetShift = { ...targetShift };

    // Swap person and store_team_member
    [newDraggedShift.person, newTargetShift.person] = [
      newTargetShift.person,
      newDraggedShift.person,
    ];
    [newDraggedShift.store_team_member, newTargetShift.store_team_member] = [
      newTargetShift.store_team_member,
      newDraggedShift.store_team_member,
    ];
    [newDraggedShift.date, newTargetShift.date] = [
      newTargetShift.date,
      newDraggedShift.date,
    ];
    [newDraggedShift.store, newTargetShift.store] = [
      newTargetShift.store,
      newDraggedShift.store,
    ];

    [newDraggedShift.role_type, newTargetShift.role_type] = [
      newTargetShift.role_type,
      newDraggedShift.role_type,
    ];

    [newDraggedShift.role_id, newTargetShift.role_id] = [
      newTargetShift.role_id,
      newDraggedShift.role_id,
    ];

    newDraggedShift.action = "add";
    newTargetShift.action = "add";
    draggedShift.action = "delete";
    targetShift.action = "delete";

    setIsLoading(true);

    // Call the addMultipleShifts function to update both shifts
    props.addMultipleShifts([
      newDraggedShift,
      newTargetShift,
      draggedShift,
      targetShift,
    ]);

    // Clear the copiedShift
    props.setCopiedShift(null);

    setIsDraggedOver(false);
  };

  // Copilot logic
  const [appliedTo, setAppliedTo] = React.useState([
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ]);

  React.useEffect(() => {
    let appliedToCopy = [false, false, false, false, false, false, false];
    appliedToCopy[props.d] = true;
    setAppliedTo(appliedToCopy);
  }, []);

  const [isCopilotSuggestion, setIsCopilotSuggestion] = React.useState(
    props.shift_info?.is_copilot_suggestion
      ? props.shift_info?.is_copilot_suggestion
      : false
  );

  React.useEffect(() => {
    setIsCopilotSuggestion(
      props.shift_info?.is_copilot_suggestion
        ? props.shift_info?.is_copilot_suggestion
        : false
    );
  }, [props.shift_info]);

  const onLongPressShift = () => {
    if (isCopilotSuggestion) {
      setIsCopilotSuggestion(false);
      props.addShift(props.shift_info, "add", appliedTo, true);
    }
  };

  const drawsSegmentsSeparately =
    !props.minimizeMode &&
    props.shifts_info?.length > 1 &&
    props.weekly_or_daily === "weekly";

  const onClickShift = (event) => {
    // Showing actuals is a read of what happened, so the card stops behaving
    // like a way into editing the plan. Classifying an absence is the
    // exception: it records the day rather than rewriting it.
    if (props.showActualTimes) {
      // Only the weekly grid draws several segments through one card, and it
      // classifies them individually below. The daily view gives each segment
      // its own card, so there the card is the shift and clicking it anywhere
      // should work. Matches the render condition exactly.
      if (!drawsSegmentsSeparately && missedShift) {
        openMissedReasonMenu(event?.currentTarget, missedShift);
      }
      return;
    }

    const readOnlyPastShift =
      props.day_in_past && !props.editShiftsInPastEnabled;

    if (readOnlyPastShift) {
      setOpen(true);
    } else if (!props.day_in_past) {
      handleOpen();
    } else if (props.editShiftsInPastEnabled) {
      setOpen(true);
    }
  };

  const deleteShift = () => {
    setIsLoading(true);
    props.addShift(props.shift_info, "delete", appliedTo, false);
  };

  const defaultOptions = {
    shouldPreventDefault: true,
    delay: 500,
  };
  const longPressEvent = useLongPress(
    onLongPressShift,
    onClickShift,
    defaultOptions
  );

  const checkTimeOffConflict = (shiftInfo, timeOffRequests) => {
    if (!timeOffRequests || timeOffRequests.length === 0) {
      return false;
    }

    // Check each time off request for overlap
    return timeOffRequests.some(request => {
      const timeOffStart = request.start_qhnum;
      const timeOffEnd = request.end_qhnum;

      // If both start_qhnum and end_qhnum are null, it's a full day off
      if (timeOffStart === null && timeOffEnd === null) {
        return true; // Any shift on this day conflicts with a full day off
      }

      // If we have specific time ranges, check for overlap
      if (timeOffStart !== null && timeOffEnd !== null) {
        const shiftStart = shiftInfo.shift_start;
        const shiftEnd = shiftInfo.shift_end;

        // Check if there's any overlap between shift and time off
        // Overlap exists if: shift_start < timeoff_end AND shift_end > timeoff_start
        return shiftStart < timeOffEnd && shiftEnd > timeOffStart;
      }

      // If only one of start_qhnum or end_qhnum is null, treat as no conflict
      // (this might need adjustment based on your business logic)
      return false;
    });
  };

  // console.log("PRESON DAY TIME OFF REQUESTS", props.person_day_time_off_requests)

  const hasTimeOffConflict = checkTimeOffConflict(props.shift_info, props.person_day_time_off_requests);
  const hasAvailabilityOrTimeOffConflict =
    ((!props.availability_info && !hasTimeOffConflict) ||
    (props.availability_info?.available_from === 0 &&
      props.availability_info?.available_to === 24 &&
      !hasTimeOffConflict)
      ? false
      : ((props.availability_info?.available_from > props.shift_info?.shift_start ||
          props.availability_info?.available_to < props.shift_info?.shift_end) &&
          (props.availability_info?.available_from_2 > props.shift_info?.shift_start ||
            props.availability_info?.available_to_2 < props.shift_info?.shift_end)) ||
        hasTimeOffConflict);
  const shiftsRepresentedByCard =
    props.weekly_or_daily === "weekly"
      ? props.shifts_info
      : [props.shift_info];
  const shiftLaborLawViolations = getShiftLaborLawViolations(
    props.laborLawViolations,
    shiftsRepresentedByCard
  );
  // Actual findings are tied to punches; planned predictions stay separate.
  const shiftWarningMessages = props.showActualTimes
    ? getActualCardWarnings(shiftsRepresentedByCard, props.actualWarningsByEntry)
    : [
    ...(hasAvailabilityOrTimeOffConflict
      ? [
          hasTimeOffConflict
            ? "Time off requested for this day"
            : "Shift outside of availability",
        ]
      : []),
    ...shiftLaborLawViolations.map(getLaborLawViolationLabel),
  ].filter((message, index, messages) => messages.indexOf(message) === index);
  // With the metrics toggle on, a card shows the times that were actually
  // worked. A shift with no matching punch keeps its scheduled times: the
  // existing attendance-review flag is what marks a shift as unaccounted for,
  // so blanking the card here would only remove information.
  // An actual card is one timecard row, so it already holds its own answer:
  // the times worked, or that nobody worked it. Nothing has to be matched back
  // to a published shift or checked against another employee's punch.
  const getActualTimes = (shift) => {
    if (!props.showActualTimes || !shift?.is_actual_card) return null;
    if (shift.is_missed) return null;
    return {
      shift_start: shift.shift_start,
      shift_end: shift.shift_end,
      in_progress: !!shift.in_progress,
    };
  };

  // A past shift that produced no timecard should stop printing the times it
  // was planned for: on the grid those read exactly like a shift somebody
  // worked. Only days the store has finished qualify, so a shift still to come
  // is never announced as missed. day_in_past is browser local and would call
  // the store's own day over as soon as the viewer's clock passed midnight,
  // accusing shifts that have not started yet.
  //
  // Worked times win over hasNoTimecard. That flag rides on actual_schedule,
  // which the punch refresh does not reload, so a punch arriving late from a
  // POS sync or a timecard correction shows up in the map while the flag still
  // says nobody clocked in. The flag is still what rules a shift unworked,
  // because a punch covering several shifts is absent from the map despite
  // having been worked.
  // No day check here: a missed card only reaches this component once its
  // scheduled end has passed, because both views filter on that before drawing
  // one. Re-testing the day would hide a shift missed this morning behind the
  // business-day rollover, so it could be seen but not classified until
  // tomorrow.
  const isUnworkedPastShift = (shift) =>
    !!props.showActualTimes &&
    !!shift?.is_actual_card &&
    !!shift?.is_missed;

  // The one thing actual mode still writes. Recording why a shift was not
  // worked is a note about what happened, not a change to the plan: it sets a
  // reason on the shift and moves nothing. Doing it from here saves crossing to
  // the planned view and back for the very gap this view exists to surface.
  const [missedReasonAnchor, setMissedReasonAnchor] = React.useState(null);
  // Which segment was clicked. A stacked cell draws several shifts through one
  // card, so classifying has to act on the one that was actually clicked rather
  // than on whichever happens to be first.
  const [missedReasonTarget, setMissedReasonTarget] = React.useState(null);
  const [savingMissedReason, setSavingMissedReason] = React.useState(false);
  // An actual card is built from the timecard payload, so its own id is
  // synthetic; the published shift is what the backend classifies against.
  const classifyTargetFor = (shift) =>
    shift?.is_actual_card ? shift?.publishedschedule?.id : shift?.id;
  const missedShift =
    isUnworkedPastShift(props.shift_info) &&
    classifyTargetFor(props.shift_info)
      ? props.shift_info
      : null;
  const canClassify = (shift) =>
    !!props.showActualTimes &&
    isUnworkedPastShift(shift) &&
    !!classifyTargetFor(shift);

  const currentMissedReason = getMissedShiftReason(missedReasonTarget);

  const openMissedReasonMenu = (element, shift) => {
    if (!element || !shift) return;
    setMissedReasonTarget(shift);
    setMissedReasonAnchor(element);
  };

  const closeMissedReasonMenu = () => {
    setMissedReasonAnchor(null);
    setMissedReasonTarget(null);
  };

  const chooseMissedReason = async (reason) => {
    const shift = missedReasonTarget;
    closeMissedReasonMenu();
    const target = classifyTargetFor(shift);
    if (!target || savingMissedReason) return;
    setSavingMissedReason(true);
    try {
      if (shift.is_actual_card) {
        await props.post_missed_shift?.(null, reason, {
          published_schedule_id: target,
        });
      } else {
        await props.post_missed_shift?.(target, reason);
      }
    } finally {
      setSavingMissedReason(false);
    }
  };

  const shiftTimesText = (shift) => {
    if (!shift) return "";
    // Prefer what the manager recorded. "Not clocked in" is the fallback for a
    // shift nobody has classified yet, and is the thing that still needs
    // looking at.
    if (isUnworkedPastShift(shift)) {
      return getMissedShiftReasonLabel(shift) || "Not clocked in";
    }
    const actual = getActualTimes(shift);
    if (!actual) {
      return (
        get_t_str_shortened(shift.shift_start) +
        NON_BREAKING_DASH +
        get_t_str_shortened(shift.shift_end) +
        business_decline_suffix(shift)
      );
    }
    // Minute precision: a punch is a measurement, and rounding it onto the
    // scheduled quarter-hour grid would hide a 11:07 clock-in as 11:00.
    //
    // An open punch comes back clamped to the current time, so printing an end
    // time would read as a finished short shift rather than one still running.
    return (
      get_t_str_shortened_minute_precision(actual.shift_start) +
      NON_BREAKING_DASH +
      (actual.in_progress
        ? "now"
        : get_t_str_shortened_minute_precision(actual.shift_end))
    );
  };

  // Only carries a tooltip when the label has been swapped away from the
  // scheduled times, so those stay one hover away.
  const renderShiftTimes = (shift, className) => {
    const classifiable = canClassify(shift);
    const label = (
      <p
        className={`${className}${classifiable ? " cursor-pointer" : ""}`}
        onClick={
          classifiable
            ? (event) => {
                // The card behind this one would otherwise classify its own
                // first shift instead of the segment that was clicked.
                event.stopPropagation();
                openMissedReasonMenu(event.currentTarget, shift);
              }
            : undefined
        }
      >
        {shiftTimesText(shift)}
      </p>
    );
    if (!getActualTimes(shift) && !isUnworkedPastShift(shift)) return label;
    return (
      <Tooltip
        title={
          shift?.is_actual_card
            ? // The tooltip is here to show the plan behind the worked times.
              // A punch nobody scheduled has no plan, and printing its own
              // clock-in under a "Scheduled" label would invent one.
              Number.isFinite(shift.published_start)
              ? `Scheduled ${get_t_str_shortened(
                  shift.published_start
                )} - ${get_t_str_shortened(shift.published_end)}${
                  shift.published_shift_count > 1
                    ? ` (${shift.published_shift_count} shifts)`
                    : ""
                }`
              : "Not scheduled"
            : `Scheduled ${get_t_str_shortened(
                shift.shift_start
              )} - ${get_t_str_shortened(shift.shift_end)}`
        }
      >
        {label}
      </Tooltip>
    );
  };

  // Asked of one shift. A stacked cell draws several, and the answer differs
  // between them: a day can hold a shift that was worked and one that was
  // missed, and a border around both says the wrong thing about each.
  const shiftIsClassifiedMissed = (shift) =>
    !!shift?.publishedschedule?.missed_shift_reason;
  // In actual mode the card's existence is the cutoff, as it is for the label:
  // a missed card is only drawn once its scheduled end has passed. Waiting for
  // the whole day to be over would let a manager classify a shift missed this
  // morning while the border that asks them to stayed off until tomorrow.
  const shiftNeedsAttendanceReview = (shift) => {
    if (!props.showActualTimes && !props.day_in_past) return false;
    const publishedSchedule = shift?.publishedschedule;
    return (
      !!publishedSchedule &&
      publishedSchedule.has_actual_used_schedule === false &&
      !publishedSchedule.missed_shift_reason &&
      // Not expected to clock in, so no clock entry is not a missed shift.
      !publishedSchedule.clock_in_optional
    );
  };
  // The border a segment carries. Every segment gets one so they stay the same
  // height; the ones with nothing to say carry a transparent one.
  const attendanceBorderFor = (shift) => {
    if (shiftIsClassifiedMissed(shift)) return "border-2 border-rose-500";
    if (shiftNeedsAttendanceReview(shift)) {
      return "border-2 border-amber-400 border-dashed";
    }
    return "border-2 border-transparent";
  };

  // What this card draws. A weekly cell draws the whole day, so it answers for
  // all of it. A daily card draws one shift and is handed the day only so it
  // knows what else is on the row, so answering for the day put the border on
  // a shift that was worked because the one beside it was missed.
  const cardDrawsWholeDay = props.weekly_or_daily === "weekly";
  const attendanceShifts =
    cardDrawsWholeDay && props.shifts_info?.length
      ? props.shifts_info
      : [props.shift_info];
  const hasClassifiedMissedShift =
    !drawsSegmentsSeparately &&
    attendanceShifts.some(shiftIsClassifiedMissed);
  const needsAttendanceReview =
    !drawsSegmentsSeparately &&
    (props.showActualTimes || props.day_in_past) &&
    attendanceShifts.some((shift) => {
      const publishedSchedule = shift?.publishedschedule;
      return (
        publishedSchedule &&
        publishedSchedule.has_actual_used_schedule === false &&
        !publishedSchedule.missed_shift_reason &&
        !publishedSchedule.clock_in_optional
      );
    });

  return (
    <>
      <style>
        {`
          @keyframes wiggle {
            0%, 100% {
              transform: rotate(-2deg);
            }
            50% {
              transform: rotate(2deg);
            }
          }
          .wiggle {
            animation: wiggle 0.3s ease-in-out infinite;
          }
        `}
      </style>
      <div className="w-full h-full relative">
        {isLoading ? (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <LoadSpinner />
          </div>
        ) : (
          <div className="w-full h-full relative">
            {/* Copilot's delete affordance has its own handler and never went
                through the click gate, so it kept deleting planned shifts from
                a read-only view. */}
            {!props.showActualTimes &&
              props.isCopilotOn &&
              props.shift_info?.copilot_suggested_deletion && (
                <Tooltip title="Copilot recommends deleting this shift - click the X to delete">
                  <div
                    className="absolute -left-2 -top-2 w-5 h-5 rounded-full bg-slate-900 text-white hover:bg-slate-200 hover:text-slate-900 flex items-center justify-center z-10 cursor-pointer"
                    onClick={deleteShift}
                  >
                    <CloseIcon style={{ fontSize: "18px" }} />
                  </div>
                </Tooltip>
              )}
            {!actionButtonsOpen || props.shift_info?.is_copilot_suggestion ? (
              <div
                {...(props.shift_info?.is_copilot_suggestion
                  ? longPressEvent
                  : { onClick: onClickShift })}
                className={`w-full h-full flex ${
                  !props.showActualTimes || missedShift
                    ? "cursor-pointer"
                    : "cursor-default"
                } ${
                  drawsSegmentsSeparately ? "" : "overflow-hidden rounded-lg"
                } ${
                  isDraggedOver ? "border-2 border-emerald-500" : ""
                } bg-white ${
                  props.showUnpublishedShifts &&
                  props.shift_info?.is_publishable
                    ? "wiggle"
                    : ""
                } shadow-md ${
                  props.weekly_or_daily === "weekly" ? "max-w-sm" : ""
                } ${
                  hasClassifiedMissedShift
                    ? "border-2 border-rose-500"
                    : needsAttendanceReview
                    ? "border-2 border-amber-400 border-dashed"
                    : ""
                }`}
                onContextMenu={(event) => handleContextMenu(event)}
                draggable={
                  !props.showActualTimes &&
                  !props.locked_mode &&
                  !props.shift_info?.is_copilot_suggestion
                }
                onDragStart={(e) => handleDragStart(e, props.shift_info)}
                onDragEnd={handleDragEnd}
                onDragOver={
                  !props.showActualTimes &&
                  !props.shift_info?.is_copilot_suggestion
                    ? handleDragOver
                    : null
                }
                onDragLeave={
                  !props.showActualTimes &&
                  !props.shift_info?.is_copilot_suggestion
                    ? handleDragLeave
                    : null
                }
                onDrop={
                  !props.showActualTimes &&
                  !props.shift_info?.is_copilot_suggestion
                    ? handleDrop
                    : null
                }
              >
                <div className={`w-full h-full relative text-white ${isCopilotSuggestion ? "opacity-40" : "opacity-100"}`}>
                  {drawsSegmentsSeparately ? (
                    <div className="w-full h-full flex flex-col gap-[1px]">
                      {props.shifts_info.map((shift, i) => {
                        const barColor = cardColorFor(shift);
                        const rounding = props.shifts_info.length === 2 ? "rounded-md" : "rounded";
                        // The whole segment classifies, not just its label: a
                        // shift is the coloured bar, and aiming at the words
                        // inside it is not what clicking a shift means.
                        const segmentClassifies = canClassify(shift);
                        return (
                          <div
                            key={i}
                            className={`w-full flex-1 flex items-center px-2 text-white ${rounding} ${barColor} ${attendanceBorderFor(
                              shift
                            )}${segmentClassifies ? " cursor-pointer" : ""}`}
                            onClick={
                              segmentClassifies
                                ? (event) => {
                                    event.stopPropagation();
                                    openMissedReasonMenu(
                                      event.currentTarget,
                                      shift
                                    );
                                  }
                                : undefined
                            }
                          >
                            {renderShiftTimes(shift, `${windowWidth <= 1279 ? "font-normal" : "font-bold"} truncate ${props.shifts_info.length >= 3 ? "text-xxs" : "text-xs"}`)}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div
                      className={`w-full h-full flex items-center py-3 px-2 text-white ${shiftCardColor}`}
                    >
                      <div className="w-full mx-1">
                        {props.weekly_or_daily === "weekly" ? (
                          <div>
                            {renderShiftTimes(props.shift_info, `text-xs ${windowWidth <= 1279 ? "font-normal" : "font-bold"}`)}
                            {props.shifts_info?.length === 1 ? (
                              <div>
                                {!props.minimizeMode && windowWidth > 1279 &&
                                  (props.shift_info.station ? (
                                    <p className="text-xxs">
                                      {props.shift_info.station.name}
                                    </p>
                                  ) : getRoleName(
                                      props.shift_info.role_id,
                                      props.role_names
                                    ) ? (
                                    <p className="text-xxs">
                                      {getRoleName(
                                        props.shift_info.role_id,
                                        props.role_names
                                      )}
                                    </p>
                                  ) : (
                                    <p className="text-xxs">
                                      {props.shift_info.role_type}
                                    </p>
                                  ))}
                              </div>
                            ) : (
                              <div>
                                {!props.minimizeMode && windowWidth > 1279 && (
                                  renderShiftTimes(props.shifts_info?.[1], `text-xs ${windowWidth <= 1279 ? "font-normal" : "font-bold"}`)
                                )}
                                {!props.minimizeMode && windowWidth > 1279 &&
                                  props.shifts_info?.length === 3 && (
                                    renderShiftTimes(props.shifts_info?.[2], `text-xs ${windowWidth <= 1279 ? "font-normal" : "font-bold"}`)
                                  )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="h-full flex items-center gap-4 pl-3">
                            {renderShiftTimes(props.shift_info, `text-xs ${windowWidth <= 1279 ? "font-normal" : "font-bold"}`)}
                            {windowWidth > 1279 && (props.shift_info.station ? (
                              <p className="text-xs">
                                {props.shift_info?.station.name}
                              </p>
                            ) : getRoleName(
                                props.shift_info.role_id,
                                props.role_names
                              ) ? (
                              <p className="text-xs">
                                {getRoleName(
                                  props.shift_info.role_id,
                                  props.role_names
                                )}
                              </p>
                            ) : (
                              <p className="text-xs">
                                {props.shift_info.role_type}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {shiftWarningMessages.length > 0 && (
                    <div
                      className={`absolute ${
                        props.minimizeMode
                          ? props.weekly_or_daily === "weekly"
                            ? "right-1 bottom-0"
                            : "right-2 bottom-0.5"
                          : props.weekly_or_daily === "weekly"
                          ? "right-2 bottom-1"
                          : "right-2 bottom-0.5"
                      }`}
                    >
                      <Tooltip
                        title={
                          shiftWarningMessages.length === 1 ? (
                            shiftWarningMessages[0]
                          ) : (
                            <ul className="list-disc pl-4">
                              {shiftWarningMessages.map((message) => (
                                <li key={message}>{message}</li>
                              ))}
                            </ul>
                          )
                        }
                      >
                        <WarningIcon
                          sx={{
                            fontSize: props.minimizeMode ? "8px" : "10px",
                          }}
                        />
                      </Tooltip>
                    </div>
                  )}
                  {props.cornerMarkers}
                  {props.shift_info?.note && (
                    <div
                      className={`absolute ${
                        props.minimizeMode
                          ? props.weekly_or_daily === "weekly"
                            ? "right-1 -top-1.5"
                            : "right-2 -top-1.5"
                          : props.weekly_or_daily === "weekly"
                          ? "right-2 -top-0.5"
                          : "right-2 -top-1.5"
                      }`}
                    >
                      <Tooltip
                        title={(() => {
                          // Collect all non-empty notes
                          const notes = [
                            props.shift_info?.note,
                            props.shifts_info?.[1]?.note,
                            props.shifts_info?.[2]?.note,
                          ].filter((note) => note);

                          // If multiple notes, create a bulleted list
                          if (notes.length > 1) {
                            return (
                              <ul>
                                {notes.map((note, index) => (
                                  <li key={index}>• {note}</li>
                                ))}
                              </ul>
                            );
                          }

                          // If only one note, return it directly
                          return notes[0];
                        })()}
                      >
                        <NoteAltIcon
                          sx={{
                            fontSize: props.minimizeMode ? "8px" : "10px",
                          }}
                        />
                      </Tooltip>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div
                className={
                  "w-full h-full flex overflow-hidden bg-white rounded-lg shadow-md " +
                  (props.weekly_or_daily === "weekly" ? "max-w-sm" : "")
                }
                onContextMenu={(event) => handleContextMenu(event)}
              >
                <div
                  className={`w-full h-full flex items-center py-3 px-2 text-white relative ${shiftCardColor}`}
                >
                  <div className="w-full mx-1">
                    <div className="w-full flex items-center justify-between space-x-1.5">
                      <Tooltip title={"Edit or add shift"} arrow>
                        <div
                          className={`flex-1 ${
                            props.weekly_or_daily === "weekly" ? "h-8" : "h-6"
                          } bg-slate-800 opacity-50 hover:bg-slate-700 rounded flex items-center justify-center`}
                          onClick={handleOpen}
                        >
                          <EditIcon style={{ fontSize: "14px" }} />
                        </div>
                      </Tooltip>
                      <Tooltip
                        title={
                          !showCopiedCheck
                            ? "Copy shift"
                            : "Shift copied. When you paste this shift to another slot, the original will remain"
                        }
                        arrow
                      >
                        <div
                          className={`flex-1 ${
                            props.weekly_or_daily === "weekly" ? "h-8" : "h-6"
                          } bg-slate-800 opacity-50 hover:bg-slate-700 rounded flex items-center justify-center`}
                          onClick={() => handleCopy(props.shift_info)}
                        >
                          {!showCopiedCheck ? (
                            <ContentCopyIcon style={{ fontSize: "14px" }} />
                          ) : (
                            <CheckIcon style={{ fontSize: "20px" }} />
                          )}
                        </div>
                      </Tooltip>
                      <Tooltip
                        title={
                          !showCutCheck
                            ? "Cut shift"
                            : "Shift cut. When you paste this shift to another slot, the original will disappear"
                        }
                        arrow
                      >
                        <div
                          className={`flex-1 ${
                            props.weekly_or_daily === "weekly" ? "h-8" : "h-6"
                          } bg-slate-800 opacity-50 hover:bg-slate-700 rounded flex items-center justify-center`}
                          onClick={() => handleCut(props.shift_info)}
                        >
                          {!showCutCheck ? (
                            <ContentCutIcon style={{ fontSize: "14px" }} />
                          ) : (
                            <CheckIcon style={{ fontSize: "20px" }} />
                          )}
                        </div>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {props.minimizeMode &&
              !actionButtonsOpen &&
              !showCopiedCheck &&
              props.shifts_info.length > 1 &&
              props.weekly_or_daily === "weekly" &&
              !(props.minimizeMode === false && props.shift_info.note) && (
                <div
                  className={`absolute ${
                    props.minimizeMode ? "right-3.5 top-0" : "right-2 top-0"
                  }`}
                >
                  <Tooltip
                    title={
                      // Minimize mode shows only the first shift, so the
                      // tooltip lists them all as the full-size card would.
                      <div>
                        <p>{props.shifts_info.length} shifts today</p>
                        <ul className="list-disc pl-4">
                          {props.shifts_info.map((shift, i) => {
                            const roleLabel =
                              shift.station?.name ||
                              getRoleName(shift.role_id, props.role_names) ||
                              shift.role_type;
                            return (
                              <li key={i}>
                                {shiftTimesText(shift)}
                                {roleLabel ? ` · ${roleLabel}` : ""}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    }
                  >
                    {props.shifts_info.length === 2 ? (
                      <Filter2Icon
                        style={{ fontSize: "10px", color: "white" }}
                      />
                    ) : (
                      <Filter3Icon
                        style={{ fontSize: "10px", color: "white" }}
                      />
                    )}
                  </Tooltip>
                </div>
              )}
            <Menu
        anchorEl={missedReasonAnchor}
        open={Boolean(missedReasonAnchor)}
        onClose={closeMissedReasonMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {[
          "called_off_sick",
          "called_off_other",
          "no_show",
          "manager_cancelled",
        ].map((reason) => (
          <MenuItem
            key={reason}
            dense
            // The reason already recorded is marked, so this doubles as a way
            // to correct one that was set wrongly.
            selected={currentMissedReason === reason}
            disabled={savingMissedReason}
            onClick={() => chooseMissedReason(reason)}
          >
            {MISSED_SHIFT_REASON_LABELS[reason]}
          </MenuItem>
        ))}
      </Menu>
      <ShiftInputsModal
              open={open}
              handleClose={handleClose}
              shift_info={props.shift_info}
              shifts_info={props.shifts_info}
              p={props.p}
              new_shift={isCopilotSuggestion ? true : false}
              addShift={props.addShift}
              date_name={props.date_name}
              availability_info={props.availability_info}
              role_names={props.role_names}
              getRoleName={getRoleName}
              getRoleType={getRoleType}
              saveShiftFinished={props.saveShiftFinished}
              setSaveShiftFinished={props.setSaveShiftFinished}
              d={props.d}
              role_types={props.role_types}
              addMultipleShifts={props.addMultipleShifts}
              businessWeekStartDayIndex={props.businessWeekStartDayIndex}
              start_qhnum={props.start_qhnum}
              schedule_day_end_qhnum={props.schedule_day_end_qhnum}
              storeTimezone={props.storeTimezone}
              frequentShifts={props.frequentShifts}
              breakWaiverEnabled={props.breakWaiverEnabled}
              person_day_time_off_requests={props.person_day_time_off_requests}
              defaultBreakLengthHours={props.defaultBreakLengthHours}
              breakRequiredWhenShiftLengthExceeds={
                props.breakRequiredWhenShiftLengthExceeds
              }
              secondBreakShiftLengthThreshold={
                props.secondBreakShiftLengthThreshold
              }
              existingShift={true}
              day_in_past={props.day_in_past}
              readOnly={props.day_in_past && !props.editShiftsInPastEnabled}
              post_missed_shift={props.post_missed_shift}
              paidBreakInputsEnabled={props.paidBreakInputsEnabled}
              businessDeclineShiftOptionEnabled={props.businessDeclineShiftOptionEnabled}
              restBreakRegulation={props.restBreakRegulation}
            />
          </div>
        )}
      </div>
    </>
  );
}

export default ShiftCard;
